// src/components/SkillsSpecial.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SkillsSpecial.css";

const SKILLS = [
  { id: "frontend", label: "Frontend", accent: "#A78BFA", items: ["React", "Next.js", "Angular", "Three.js", "HTML/CSS/JS", ] },
  { id: "backend", label: "Backend", accent: "#34D399", items: ["FastAPI", "Flask", "Node.js", "REST APIs"] },
  { id: "ai", label: "AI / LLM", accent: "#F472B6", items: ["LangChain", "LangGraph", "RAG", "Prompt Engineering"] },
  { id: "cloud", label: "Cloud / Infra", accent: "#FBBF24", items: ["AWS", "Docker", "CI/CD", "Heroku", "Vercel"] },
  { id: "db", label: "Data / DB", accent: "#60A5FA", items: ["SQL", "Supabase", "Firebase", "MongoDB"] },
  { id: "prog", label: "Programming", accent: "#7DD3FC", items: ["Python", "C++", "C", "Java", "TypeScript"] },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

function useSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function len(x, y) {
  return Math.sqrt(x * x + y * y) || 1;
}

function makeCurve(x1, y1, x2, y2, bendSign = 1) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = len(dx, dy);

  const ux = dx / d;
  const uy = dy / d;
  const px = -uy;
  const py = ux;

  const pull = clamp(d * 0.34, 40, 160);
  const bend = clamp(d * 0.14, 14, 44) * bendSign;

  const c1x = x1 + ux * pull + px * bend * 0.12;
  const c1y = y1 + uy * pull + py * bend * 0.12;
  const c2x = x2 - ux * pull + px * bend * 0.6;
  const c2y = y2 - uy * pull + py * bend * 0.6;

  return `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
}

/**
 * Uniform parent->child distance:
 * Place children on an outward-facing arc around the parent at fixed radius.
 */
function computeChildNodes({ parent, center, items, w, h, childRadiusPx, arcDeg, maxItems }) {
  if (!parent) return [];
  const max = Math.min(items.length, maxItems);

  const cx = center.x;
  const cy = center.y;
  const px = parent.x;
  const py = parent.y;

  // outward direction from center -> parent
  const dx = px - cx;
  const dy = py - cy;
  const d = len(dx, dy);
  const ux = dx / d;
  const uy = dy / d;

  // angle of outward direction
  const base = Math.atan2(uy, ux);

  // distribute on an arc centered on "base"
  const arc = (arcDeg * Math.PI) / 180;
  const start = base - arc / 2;
  const step = max === 1 ? 0 : arc / (max - 1);

  const pad = 26;

  const nodes = [];
  for (let i = 0; i < max; i++) {
    const a = start + step * i;

    // FIXED LENGTH from parent
    const x = px + Math.cos(a) * childRadiusPx;
    const y = py + Math.sin(a) * childRadiusPx;

    nodes.push({
      id: `${parent.id}__${items[i]}`,
      label: items[i],
      x: clamp(x, pad, w - pad),
      y: clamp(y, pad, h - pad),
      bendSign: i % 2 === 0 ? 1 : -1,
    });
  }
  return nodes;
}

export default function SkillsSpecial({ hideTitle = false }) {
  const [activeId, setActiveId] = useState(null);
  const reducedMotion = usePrefersReducedMotion();

  const mapRef = useRef(null);
  const { w, h } = useSize(mapRef);

  const [pointer, setPointer] = useState({ x: 0.5, y: 0.5 });

  const active = useMemo(() => SKILLS.find((d) => d.id === activeId) || null, [activeId]);

  const layout = useMemo(() => {
    if (!w || !h) return { center: { x: 0, y: 0 }, nodes: [] };

    const center = { x: w * 0.5, y: h * 0.54 };

    // spread domains
    const r = clamp(Math.min(w, h) * 0.42, 260, 390);
    const n = SKILLS.length;
    const start = -Math.PI / 2;
    const step = (Math.PI * 2) / n;

    const nodes = SKILLS.map((d, i) => {
      const ang = start + step * i;
      const ox = Math.cos(ang) * r * 1.12;
      const oy = Math.sin(ang) * r * 0.82;

      return {
        ...d,
        x: clamp(center.x + ox, 170, w - 170),
        y: clamp(center.y + oy, 140, h - 140),
        bendSign: i % 2 === 0 ? 1 : -1,
      };
    });

    return { center, nodes };
  }, [w, h]);

  const activeNode = useMemo(() => layout.nodes.find((n) => n.id === activeId) || null, [layout.nodes, activeId]);

  const domainLines = useMemo(() => {
    if (!layout.nodes.length) return [];
    return layout.nodes.map((n) => ({
      id: n.id,
      accent: n.accent,
      d: makeCurve(layout.center.x, layout.center.y, n.x, n.y, n.bendSign),
    }));
  }, [layout]);

  const CHILD_RADIUS_PX = useMemo(() => clamp(Math.min(w, h) * 0.18, 130, 200), [w, h]);

  const childNodes = useMemo(() => {
    if (!active || !activeNode || !w || !h) return [];
    return computeChildNodes({
      parent: activeNode,
      center: layout.center,
      items: active.items,
      w,
      h,
      childRadiusPx: CHILD_RADIUS_PX,
      arcDeg: 120,
      maxItems: 8,
    });
  }, [active, activeNode, w, h, layout.center, CHILD_RADIUS_PX]);

  const childLines = useMemo(() => {
    if (!activeNode || !childNodes.length) return [];
    return childNodes.map((c) => ({
      id: c.id,
      d: makeCurve(activeNode.x, activeNode.y, c.x, c.y, c.bendSign),
    }));
  }, [activeNode, childNodes]);

  const onMove = (e) => {
    if (reducedMotion) return;
    const el = mapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPointer({
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
    });
  };

  const parallaxStyle = reducedMotion
    ? {}
    : {
        "--px": `${(pointer.x - 0.5) * 2}`,
        "--py": `${(pointer.y - 0.5) * 2}`,
      };

  const activate = (id) => setActiveId(id);
  const toggle = (id) => setActiveId((cur) => (cur === id ? null : id));

  return (
    <section className="ssWrapNew" aria-label="Skills section">
      {!hideTitle && (
        <header className="ssHeadNew">
          <h2 className="ssTitleNew">Skills</h2>
          <p className="ssSubNew">Eager to learn more :)</p>
        </header>
      )}

      <div
        ref={mapRef}
        className="ssMap"
        style={parallaxStyle}
        onMouseMove={onMove}
        onMouseLeave={() => setActiveId(null)}
        role="img"
        aria-label="Skills systems map"
      >
        <div className="ssBackdrop" aria-hidden="true" />

        {w > 0 && h > 0 && (
          <svg className="ssSvg" width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
            {/* center -> domains */}
            {domainLines.map((ln) => (
              <path
                key={ln.id}
                d={ln.d}
                className={
                  "ssLine " +
                  (activeId === ln.id ? "ssLineActive" : "") +
                  (activeId && activeId !== ln.id ? " ssLineDim" : "")
                }
                style={{ "--accent": ln.accent }}
              />
            ))}

            {/* active domain -> children */}
            {active &&
              childLines.map((ln) => (
                <path key={ln.id} d={ln.d} className="ssLine ssLineBranch" style={{ "--accent": active.accent }} />
              ))}
          </svg>
        )}

        {/* ===== Brain Core (Option A) ===== */}
        {w > 0 && h > 0 && (
          <div
            className={"ssBrainCore " + (activeId ? "ssBrainCoreQuiet" : "")}
            style={{
              left: `${layout.center.x}px`,
              top: `${layout.center.y}px`,
              "--accent": active?.accent || "#ffffff",
            }}
            aria-hidden="true"
          >
            <svg className="ssBrainSvg" viewBox="0 0 64 64" aria-hidden="true">
              {/* outer brain-ish shape */}
              <path
                className="ssBrainOutline"
                d="M24.5 14.2c-3.2.6-5.7 2.4-7.2 5.1-2.7.5-4.9 2.2-6.1 4.6-1.2 2.3-1.3 5-.3 7.4-.9 1.8-1 3.9-.3 5.8.8 2.2 2.6 3.9 4.9 4.6.5 2.6 2.2 4.9 4.7 6.1 2.4 1.2 5.2 1.2 7.6 0 1.3 1.6 3.3 2.6 5.5 2.6V12.6c-1.4-1-3.2-1.3-4.8-1.1z"
              />
              <path
                className="ssBrainOutline"
                d="M39.5 14.2c3.2.6 5.7 2.4 7.2 5.1 2.7.5 4.9 2.2 6.1 4.6 1.2 2.3 1.3 5 .3 7.4.9 1.8 1 3.9.3 5.8-.8 2.2-2.6 3.9-4.9 4.6-.5 2.6-2.2 4.9-4.7 6.1-2.4 1.2-5.2 1.2-7.6 0-1.3 1.6-3.3 2.6-5.5 2.6V12.6c1.4-1 3.2-1.3 4.8-1.1z"
              />

              {/* inner “neural” lines */}
              <path className="ssBrainTrace" d="M20 22c2.8 0 4.2 1.6 4.2 3.3 0 1.8-1.2 3-3.1 3.4" />
              <path className="ssBrainTrace" d="M18.5 34c2.2-.2 3.6 1.2 3.9 2.7.4 1.8-.8 3.4-3 3.9" />
              <path className="ssBrainTrace" d="M44 22c-2.8 0-4.2 1.6-4.2 3.3 0 1.8 1.2 3 3.1 3.4" />
              <path className="ssBrainTrace" d="M45.5 34c-2.2-.2-3.6 1.2-3.9 2.7-.4 1.8.8 3.4 3 3.9" />
              <path className="ssBrainMid" d="M32 14v36" />

              {/* nodes */}
              <circle className="ssBrainNode" cx="24.5" cy="25.2" r="1.6" />
              <circle className="ssBrainNode" cx="21.2" cy="28.9" r="1.5" />
              <circle className="ssBrainNode" cx="20.2" cy="36.0" r="1.6" />
              <circle className="ssBrainNode" cx="39.5" cy="25.2" r="1.6" />
              <circle className="ssBrainNode" cx="42.8" cy="28.9" r="1.5" />
              <circle className="ssBrainNode" cx="43.8" cy="36.0" r="1.6" />
              <circle className="ssBrainNucleus" cx="32" cy="30" r="2.2" />
            </svg>
          </div>
        )}

        {!activeId && <div className="ssHintFloating">Hover a domain to expand its branches.</div>}

        {/* Domain nodes */}
        {layout.nodes.map((n) => (
          <button
            key={n.id}
            type="button"
            className={
              "ssNode " +
              (activeId === n.id ? "ssNodeActive" : "") +
              (activeId && activeId !== n.id ? " ssNodeDim" : "")
            }
            style={{ left: `${n.x}px`, top: `${n.y}px`, "--accent": n.accent }}
            onMouseEnter={() => activate(n.id)}
            onFocus={() => activate(n.id)}
            onClick={() => toggle(n.id)}
            aria-label={`Skill domain: ${n.label}`}
            aria-pressed={activeId === n.id}
          >
            <span className="ssNodeLabel">{n.label}</span>
          </button>
        ))}

        {/* Child chips */}
        {active &&
          childNodes.map((c) => (
            <div key={c.id} className="ssItemNode" style={{ left: `${c.x}px`, top: `${c.y}px`, "--accent": active.accent }} aria-hidden="true">
              {c.label}
            </div>
          ))}

        <div className="ssVignette" aria-hidden="true" />
      </div>

      {/* Mobile accordion */}
      <div className="ssStack" role="list" aria-label="Skills list">
        {SKILLS.map((d) => {
          const open = activeId === d.id;
          return (
            <div key={d.id} role="listitem" className={"ssCard " + (open ? "ssCardOpen" : "")} style={{ "--accent": d.accent }}>
              <button type="button" className="ssCardHead" onClick={() => toggle(d.id)} aria-expanded={open}>
                <span className="ssCardTitle">{d.label}</span>
                <span className="ssChevron" aria-hidden="true">{open ? "−" : "+"}</span>
              </button>
              <div className="ssCardBody">
                <div className="ssChips">
                  {d.items.map((t) => (
                    <span key={t} className="ssChip">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
